import { type DropdownListItem } from '@verantos-dev/shared-components';
import {
  type ActiveIndexDateItemLegacy,
  type InputFieldConfig,
  InputFieldTypeLegacy,
  type InputOptionContextDataLegacy,
} from '@verantos-dev/types-cohorts';
import {
  Index_dateContext,
  Index_date_definitionContext,
} from '@verantos-dev/util-cohort-builder-typescript-runtime';

import { TOKEN_OFFSET } from '../../constants/constants';
import { tokenTypeForTokenMap, tokenOptionsForTokenMap } from '../../constants/runtimeMaps';

export const getActiveCohortBuilderItemStateForIndexDate = (
  indexDateContext: Index_dateContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveIndexDateItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveIndexDateItemLegacy => {
  const indexDateDefinitionContext = indexDateContext.index_date_definition();

  if (indexDateDefinitionContext) {
    const filterIndex = (indexDateDefinitionContext.start?.tokenIndex || 0) + TOKEN_OFFSET;

    // get next index date value and input config
    const inputFieldConfigMap: Record<string, InputFieldConfig> = {};
    const indexDateValueOptions = codeCompletionOptions[filterIndex];
    inputFieldConfigMap[nextActiveCohortBuilderItem.id] = {
      inputType: getInputTypeForIndexDate(indexDateValueOptions),
      options: getOptionsForIndexDate(indexDateValueOptions, inputOptionContextData),
      label: getLabelForIndexDate(indexDateDefinitionContext),
      placeholder: getPlaceholderForIndexDate(indexDateDefinitionContext),
      width: 'auto',
    };

    return {
      ...nextActiveCohortBuilderItem,
      inputFieldConfigMap,
    };
  }

  return nextActiveCohortBuilderItem;
};

const getInputTypeForIndexDate = (options: string[]): InputFieldTypeLegacy => {
  const inputTypes = options.map((option) => tokenTypeForTokenMap[option]);
  if (inputTypes.includes(InputFieldTypeLegacy.COMBO_BOX)) {
    return InputFieldTypeLegacy.COMBO_BOX;
  }
  if (inputTypes.includes(InputFieldTypeLegacy.DATE_PICKER)) {
    return InputFieldTypeLegacy.DATE_PICKER;
  }
  return InputFieldTypeLegacy.DROPDOWN;
};

const getOptionsForIndexDate = (
  options: string[],
  { eventNames }: InputOptionContextDataLegacy
): DropdownListItem[] => {
  const instanceEvents = eventNames?.filter((event) =>
    event.value?.includes('INSTANCE_EVENT::')
  ) as DropdownListItem[] | undefined;
  return options.reduce((accu: DropdownListItem[], optionToken) => {
    // We get the options for the token from the tokenOptionsForTokenMap
    const optionsForToken: DropdownListItem[] = tokenOptionsForTokenMap[optionToken]?.reduce(
      (_optionsForToken: DropdownListItem[], { displayValue, grammarValue }) => {
        if (grammarValue === 'INSTANCE_EVENT_NAME') {
          return instanceEvents ? [..._optionsForToken, ...instanceEvents] : _optionsForToken;
        }
        return [
          ..._optionsForToken,
          {
            id: grammarValue.toLowerCase().replace(/ /g, '_'),
            value: grammarValue,
            displayValue,
          },
        ];
      },
      []
    );
    return [...accu, ...(optionsForToken ?? [])];
  }, []);
};

const getLabelForIndexDate = (indexDateDefinitionContext: Index_date_definitionContext): string => {
  if (indexDateDefinitionContext.calendarError() || indexDateDefinitionContext.calendar_date()) {
    return 'Date';
  }
  if (
    indexDateDefinitionContext.index_event_date() ||
    indexDateDefinitionContext.index_event_date_error()
  ) {
    return 'Event';
  }

  throw new Error('No label found for index date definition context:' + indexDateDefinitionContext);
};

const getPlaceholderForIndexDate = (
  indexDateDefinitionContext: Index_date_definitionContext
): string => {
  if (indexDateDefinitionContext.calendarError() || indexDateDefinitionContext.calendar_date()) {
    return '';
  }
  if (
    indexDateDefinitionContext.index_event_date() ||
    indexDateDefinitionContext.index_event_date_error()
  ) {
    return 'Select Event';
  }

  throw new Error('No label found for index date definition context:' + indexDateDefinitionContext);
};
